import React, { useState } from 'react';
import { TaskCategory } from '../types';
import styles from '../../../../styles/PointScheme.module.css';
import buttonStyles from '../../../../styles/Buttons.module.css';

interface CategoryCardProps {
  category: TaskCategory;
  onEdit: (category: TaskCategory) => void;
  onDelete: (categoryId: string) => void;
  canEdit?: boolean;
  canDelete?: boolean;
}

const CategoryCard: React.FC<CategoryCardProps> = ({ category, onEdit, onDelete, canEdit = true, canDelete = true }) => {
  const [showFullDescription, setShowFullDescription] = useState(false);
  const rules = category.scheme_configuration?.deadline_rules || category.scheme_config?.deadline_rules || [];
  const basePoints = category.scheme_configuration?.base_points || category.default_points || 0;
  const priority = category.priority || 'medium';

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      });
    } catch {
      return 'N/A';
    }
  };

  const formatPriority = (priority: string) => {
    return priority
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const description = category.description || 'No description available';
  const maxDescriptionLength = 120;
  const shouldTruncate = description.length > maxDescriptionLength;
  const displayDescription = showFullDescription || !shouldTruncate 
    ? description 
    : `${description.substring(0, maxDescriptionLength)}...`;

  return (
    <div className={styles.categoryCard}>
      {/* Card Header */}
      <div className={styles.cardHeader}>
        <div className={styles.cardHeaderLeft}>
          <div className={styles.categoryIconWrapper} style={{ backgroundColor: `${category.color}20` }}>
            <i 
              className={`bi bi-${category.icon}`} 
              style={{ color: category.color, fontSize: '1.5rem' }}
            ></i>
          </div>
          <div className={styles.cardTitleSection}>
            <h3 className={styles.cardTitle}>{category.name}</h3>
            <div className={styles.cardDescription}>
              {displayDescription}
              {shouldTruncate && (
                <button 
                  className={styles.moreLink}
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowFullDescription(!showFullDescription);
                  }}
                >
                  {showFullDescription ? ' - Less' : '+ More'}
                </button>
              )}
            </div>
          </div>
        </div>
        <div className={styles.cardHeaderRight}>
          <div className={styles.cardActions}>
            {canEdit && (
            <button 
              className={`${buttonStyles.btn} ${buttonStyles.btnSecondary} ${buttonStyles.btnIcon}`}
              onClick={(e) => {
                e.stopPropagation();
                onEdit(category);
              }}
              title="Edit"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
              </svg>
            </button>
            )}
            {canDelete && (
            <button 
              className={`${buttonStyles.btn} ${buttonStyles.btnDanger} ${buttonStyles.btnIcon}`}
              onClick={(e) => {
                e.stopPropagation();
                onDelete(category.id);
              }}
              title="Delete"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="3,6 5,6 21,6"></polyline>
                <path d="M19,6v14a2,2 0 0,1 -2,2H7a2,2 0 0,1 -2,-2V6m3,0V4a2,2 0 0,1 2,-2h4a2,2 0 0,1 2,2v2"></path>
                <line x1="10" y1="11" x2="10" y2="17"></line>
                <line x1="14" y1="11" x2="14" y2="17"></line>
              </svg>
            </button>
            )}
          </div>
        </div>
      </div>

      {/* Card Body - Tags and Badges */}
      <div className={styles.cardBody}>
        <div className={styles.cardTags}>
          <div 
            className={styles.categoryTag}
            style={{ backgroundColor: `${category.color}15`, color: category.color, borderColor: `${category.color}40` }}
          >
            <i className={`bi bi-${category.icon}`} style={{ marginRight: '0.375rem', fontSize: '0.75rem' }}></i>
            <span style={{ fontSize: '0.625rem' }}>{category.name}</span>
          </div>
          
          

          <div className={`${styles.priorityTag} ${styles[`priority${priority.charAt(0).toUpperCase() + priority.slice(1)}`]}`}>
            <span style={{ fontSize: '0.625rem' }}>{formatPriority(priority)} Priority</span>
          </div>
          <div className={styles.pointsTag}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '0.375rem' }}>
              <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
            </svg>
            <span style={{ fontSize: '0.625rem' }}>{basePoints} Points</span>
          </div>

          {rules.length > 0 && (() => {
            // Find the earliest deadline rule (before deadline with smallest time)
            const beforeRules = rules.filter((r: any) => r.time_period_type === 'before' && r.time_before);
            if (beforeRules.length > 0) {
              const earliestRule = beforeRules.reduce((earliest: any, current: any) => {
                const earliestDays = (earliest.time_before?.days || 0) + 
                                   ((earliest.time_before?.hours || 0) / 24) + 
                                   ((earliest.time_before?.minutes || 0) / 1440);
                const currentDays = (current.time_before?.days || 0) + 
                                  ((current.time_before?.hours || 0) / 24) + 
                                  ((current.time_before?.minutes || 0) / 1440);
                return currentDays < earliestDays ? current : earliest;
              });
              
              const days = earliestRule.time_before?.days || 0;
              const hours = earliestRule.time_before?.hours || 0;
              const minutes = earliestRule.time_before?.minutes || 0;
              
              let deadlineText = '';
              if (days > 0) {
                deadlineText = `${days} day${days !== 1 ? 's' : ''}`;
              } else if (hours > 0) {
                deadlineText = `${hours} hour${hours !== 1 ? 's' : ''}`;
              } else if (minutes > 0) {
                deadlineText = `${minutes} min${minutes !== 1 ? 's' : ''}`;
              }
              
              if (deadlineText) {
                return (
                  <div className={styles.deadlineTag}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '0.375rem' }}>
                      <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                      <line x1="16" y1="2" x2="16" y2="6"></line>
                      <line x1="8" y1="2" x2="8" y2="6"></line>
                      <line x1="3" y1="10" x2="21" y2="10"></line>
                    </svg>
                    <span style={{ fontSize: '0.625rem' }}>{deadlineText}</span>
                  </div>
                );
              }
            }
            return null;
          })()}
        </div>

        {/* Rules and Metadata */}
        <div className={styles.cardMetadata}>
          {rules.length > 0 && (
            <div className={styles.metadataItem}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={styles.metadataIcon}>
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                <polyline points="14 2 14 8 20 8"></polyline>
                <line x1="16" y1="13" x2="8" y2="13"></line>
                <line x1="16" y1="17" x2="8" y2="17"></line>
                <polyline points="10 9 9 9 8 9"></polyline>
              </svg>
              <span className={styles.metadataText}>{rules.length} Deadline Rule{rules.length !== 1 ? 's' : ''}</span>
            </div>
          )}

          <div className={styles.metadataItem}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={styles.metadataIcon}>
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
              <line x1="16" y1="2" x2="16" y2="6"></line>
              <line x1="8" y1="2" x2="8" y2="6"></line>
              <line x1="3" y1="10" x2="21" y2="10"></line>
            </svg>
            <span className={styles.metadataText}>Created: {formatDate(category.created_at)}</span>
          </div>

          {category.updated_at !== category.created_at && (
            <div className={styles.metadataItem}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={styles.metadataIcon}>
                <circle cx="12" cy="12" r="10"></circle>
                <polyline points="12 6 12 12 16 14"></polyline>
              </svg>
              <span className={styles.metadataText}>Updated: {formatDate(category.updated_at)}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CategoryCard;
